import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from '../ui/dialog';
import { addDays, addYears, format, setDate, setMonth, setYear } from 'date-fns';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import Select from 'src/components/ui/BrandSelect';
import { Actions as ACTIONS } from '../../store/actions/reportActions';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';

import { getClientUnusableReason, isClientUsable, isConsultantClientOrg } from '../utils/consultantClientUtils';
import WarningModal from './WarningModal';
import { CustomOption } from '../Common/ReusableComponents/Reusable';

const ConfirmModal = ({
  openModal,
  setOpenModal,
  reportingDates,
  addReport,
  showReportModal,
  subDetails,
  setReportingDates,
}) => {
  const [loading, setLoading] = useState(false);
  const [cardDeclined, setCardDeclined] = useState(false);
  const history = useHistory();
  const handleClose = () => {
    showReportModal(null);
    setOpenModal(false);
    if (cardDeclined) setCardDeclined(false);
  };

  return (
    <Dialog open={openModal} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent onClose={handleClose} className='max-w-md p-0 bg-background'>
        <DialogHeader className='px-6 pt-6 pb-4 pr-14 border-b border-border'>
          <DialogTitle className='font-display text-lg'>Please confirm the billing</DialogTitle>
        </DialogHeader>
        <DialogBody className='max-h-[min(70vh,480px)] overflow-y-auto'>
          <p className='font-semibold text-foreground mb-4'>
            Please click &quot;confirm&quot; to verify that your payment method on file will be charged {subDetails?.subPrice}{' '}
            {subDetails?.curr} to generate a new report, and that your subscription start will be re-set to today&apos;s date.
          </p>
          {cardDeclined && (
            <Alert variant='destructive' className='mb-4'>
              <AlertDescription>
                <span className='font-semibold text-destructive'>
                  Your card on file cannot be processed, please update your card details{' '}
                  <button
                    type='button'
                    className='underline text-destructive hover:text-destructive/80'
                    onClick={() => history.push('/profile')}>
                    here
                  </button>
                  , and re-run the report.
                </span>
              </AlertDescription>
            </Alert>
          )}
          <Button
            disabled={!reportingDates || loading || cardDeclined}
            onClick={async () => {
              try {
                setLoading(true);
                const { error } = (await addReport(reportingDates)) || {};
                if (error?.includes('card')) setCardDeclined(true);
                else handleClose();
              } catch (err) {
                console.log('err', err);
              } finally {
                setLoading(false);
                setReportingDates(null);
              }
            }}>
            {loading && <Loader2 size={16} strokeWidth={1.5} className='animate-spin mr-2' />}
            Confirm
          </Button>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const NonStripeModal = ({ openModal, setOpenModal, showReportModal, supportEmail }) => {
  const handleClose = () => {
    showReportModal(null);
    setOpenModal(false);
  };

  return (
    <Dialog open={openModal} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent onClose={handleClose} className='max-w-md p-0 bg-background'>
        <DialogHeader className='px-6 pt-6 pb-4 pr-14 border-b border-border'>
          <DialogTitle className='font-display text-lg'>Please contact support</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className='font-semibold text-foreground mb-4'>
            Please contact support to update your subscription to generate a new report. This means that your subscription
            start date will be re-set to the payment&apos;s date.
          </p>
          <Button
            onClick={() => {
              window.open(`mailto:${supportEmail}`);
              handleClose();
            }}>
            Email Support
          </Button>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const AddReport = ({
  addReport,
  hideAddReportModal,
  showModal,
  filteredReportOptions,
  noExtraReport,
  updateSubscriptionToGenerateReport,
  showReportModal,
  nonStripeUser,
  supportEmail,
  admin,
  consultant,
  directUser,
  clientReportBlocked,
  clientReportBlockedReason,
}) => {
  const [subDetails, setSubDetails] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [reportingDates, setReportingDates] = useState(null);
  const [disableBtn, setDisableBtn] = useState(false);
  const [showNonStripeModal, setShowNonStripeModal] = useState(false);

  const handleCloseMain = () => {
    hideAddReportModal();
    setReportingDates(null);
  };

  if (!showModal) return null;

  return (
    <>
      <Dialog open={!!showModal} onOpenChange={(open) => { if (!open) handleCloseMain(); }}>
        <DialogContent
          onClose={handleCloseMain}
          className='flex max-h-[90vh] w-full max-w-lg flex-col p-0 bg-background font-body text-xs sm:text-sm'
          style={{ overflow: 'visible' }}>
          <DialogHeader className='shrink-0 border-b border-border px-6 pb-4 pt-6 pr-14'>
            <DialogTitle className='font-display text-lg'>Add Report</DialogTitle>
          </DialogHeader>
          <DialogBody className='min-h-0 flex-1 overflow-y-auto px-6 py-5'>
            {clientReportBlocked && !admin && (
              <Alert variant='warning' className='mb-4'>
                <AlertDescription>{clientReportBlockedReason}</AlertDescription>
              </Alert>
            )}
            <div className={noExtraReport ? 'hidden' : 'mb-4'}>
              <label htmlFor='reportDatesSelect' className='mb-1 block text-sm font-semibold text-foreground'>
                Reporting Dates
              </label>
              <Select
                id='reportDatesSelect'
                inputId='reportDatesSelect'
                onChange={setReportingDates}
                options={filteredReportOptions}
                isClearable={false}
                maxMenuHeight={240}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition='fixed'
                components={{ Option: CustomOption('reportsDatesSelect') }}
                styles={{
                  container: (base) => ({ ...base, zIndex: 10 }),
                }}
              />
            </div>

            {noExtraReport && (
              <p className='mb-4 text-sm text-foreground'>
                Please contact TerraVerde at{' '}
                <a className='text-primary underline hover:text-primary/90' href='mailto:hello@terraverde-solutions.com'>
                  hello@terraverde-solutions.com
                </a>
                .
              </p>
            )}

            {reportingDates?.financialReportDate && (
              <Alert variant='warning' className='mb-4'>
                <AlertDescription>
                  When your reporting period is not a calendar year, we will create an extra report for free to cover the
                  calendar year {format(reportingDates.calendarReportDate.from, 'do MMM yyyy')} to{' '}
                  {format(reportingDates.calendarReportDate.to, 'do MMM yyyy')} — should you need to report on both.
                </AlertDescription>
              </Alert>
            )}

            {updateSubscriptionToGenerateReport && directUser && !nonStripeUser && (
              <WarningModal setSubDetails={setSubDetails} subDetails={subDetails} />
            )}

            {!noExtraReport && (
              <Button
                id='report-add-btn'
                className='mt-2'
                disabled={!reportingDates || disableBtn || (clientReportBlocked && !admin)}
                onClick={async () => {
                  if (nonStripeUser && directUser && !admin) {
                    setShowNonStripeModal(true);
                  } else if (updateSubscriptionToGenerateReport && directUser && !nonStripeUser) {
                    setShowConfirmModal(true);
                  } else {
                    setDisableBtn(true);
                    await addReport(reportingDates);
                    setDisableBtn(false);
                  }
                }}>
                {updateSubscriptionToGenerateReport && directUser && !nonStripeUser
                  ? 'Update Subscription & Add a Report'
                  : 'Add a Report'}
              </Button>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      {showConfirmModal && (
        <ConfirmModal
          openModal={showConfirmModal}
          setOpenModal={setShowConfirmModal}
          reportingDates={reportingDates}
          setReportingDates={setReportingDates}
          addReport={addReport}
          showReportModal={showReportModal}
          subDetails={subDetails}
        />
      )}

      {showNonStripeModal && (
        <NonStripeModal
          openModal={showNonStripeModal}
          setOpenModal={setShowNonStripeModal}
          showReportModal={showReportModal}
          supportEmail={supportEmail}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  let noExtraReport = false;

  const showModal = state.report?.modal;
  const updateSubscriptionToGenerateReport = state.report?.updateSubscription;
  const reportList = state.report?.list;
  const { whiteLabelAdmin } = state.profile?.details || {};
  const consultantId = state.organisation?.details?.consultantId;

  if (reportList.error) return;

  const calendarYearReports = state.report?.list?.filter(
    (report) => report.endDate.includes(report.year) && report.startDate.includes(report.year),
  );
  const orgDetails = state.organisation?.details;
  const whiteLabelId = state.organisation?.details?.whiteLabelId;

  if (whiteLabelId === '263247' && calendarYearReports?.length >= 2) {
    // Each client for TerraVerde can only add two reports. Only TerraVerde admins can add extra reports
    noExtraReport = whiteLabelAdmin === '263247' ? false : true;
  }

  if (!orgDetails) return { showModal: false };

  const maxReportingStartDate = addDays(
    setDate(setMonth(new Date(), (orgDetails.reportingYearEndMonth || 12) - 1), orgDetails.reportingYearEndDay || 31),
    1,
  );

  const maxYear = parseInt(format(maxReportingStartDate, 'yyyy'));
  const financialReportDates = [];
  const calendarReportDates = [];

  for (var year = maxYear; year >= 2014; year--) {
    let calendarYear = year;
    if (orgDetails.reportingYearEndMonth > 6 && orgDetails.reportingYearEndMonth !== 12) {
      // calendar year to be less 1 to make sense
      calendarYear += 1;
    }
    calendarReportDates.push({
      from: new Date(`${calendarYear}-01-01`),
      to: new Date(`${calendarYear}-12-31`),
    });

    if (orgDetails.reportingYearEndMonth !== '12') {
      const financialYearStart = setYear(maxReportingStartDate, year);

      financialReportDates.push({
        from: financialYearStart,
        to: addDays(addYears(financialYearStart, 1), -1),
      });
    }
  }

  const reportOptions = [];

  calendarReportDates.forEach((calendarReportDate, index) => {
    const financialReportDate = financialReportDates[index];
    const dateForString = financialReportDate || calendarReportDate;
    const dateString = `${format(dateForString.from, 'do MMM yyyy')} to ${format(dateForString.to, 'do MMM yyyy')}`;

    reportOptions.push({
      calendarReportDate,
      financialReportDate,
      label: dateString,
      value: dateString,
    });
  });

  const reportListDates = Array.isArray(reportList) && reportList?.map((date) => new Date(date.startDate).toISOString());
  const filteredReportOptions =
    reportListDates &&
    reportOptions?.filter((report) => !reportListDates.includes(report.calendarReportDate.from.toISOString()));

  const customerId = state.payment?.stripe?.stripeCustomerId;
  const nonStripeUser = customerId === '' ? true : false;

  const supportEmail = state.whiteLabelAdmin?.supportEmail;
  const admin = state.profile?.details?.cyfAdmin || false;
  const accountType = state.organisation?.details?.accountType;

  let consultant;
  if (consultantId && consultantId != null && consultantId != '' && consultantId != 'NONE') {
    consultant = true;
  } else {
    consultant = false;
  }

  let whiteLabel;
  if (whiteLabelId && whiteLabelId != '0') {
    whiteLabel = true;
  } else {
    whiteLabel = false;
  }

  let directUser;
  if (accountType && accountType == 'Direct User') {
    directUser = true;
  } else {
    directUser = false;
  }

  const consultantParentId = state.profile?.details?.consultantAdmin;
  const orgDetailsFull = state.organisation?.details || {};
  const isConsultantClient = isConsultantClientOrg(orgDetailsFull, consultantParentId);
  const clientReportBlocked = isConsultantClient && !isClientUsable(orgDetailsFull);
  const clientReportBlockedReason = clientReportBlocked ? getClientUnusableReason(orgDetailsFull) : null;

  return {
    showModal,
    reportList,
    filteredReportOptions,
    noExtraReport,
    updateSubscriptionToGenerateReport,
    nonStripeUser,
    supportEmail,
    admin: admin,
    consultant,
    whiteLabel,
    directUser,
    clientReportBlocked,
    clientReportBlockedReason,
  };
};

const mapDispatchToProps = (dispatch) => ({
  addReport: (dates) => dispatch(ACTIONS.addReport(dates)),
  hideAddReportModal: () => dispatch(ACTIONS.setAddReportModal(false)),
  showReportModal: (status) => dispatch(ACTIONS.setAddReportModal(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddReport);
